import React, { useState, useEffect } from "react";
import { useForm, Controller } from "react-hook-form";
import Select from "react-select";
import ToggleSwitch from "../../components/ToggleSwitch/ToggleSwitch";
import "react-sliding-pane/dist/react-sliding-pane.css";
import {
  useCreateOrUpdateEmployeeSalaryComponents,
  useViewEmployeeGeneratedSalaryComponents,
  useViewStatutoryComponents,
} from "../../queries/payrollQuries";
import { toast } from "react-toastify";
import {
  calculationFromOptions,
  componentTypeOptions,
  calculationTypeOptions,
} from "../../utils/commonSelectBoxOptions/commonSelectBoxOptions";
import {
  SALARY_COMPONENT_CALCULATION,
  SALARY_COMPONENT_TYPES,
} from "../../../src/redux/constants/payrollComponents";
import { Tooltip } from "antd";
import { infoIcon } from "../../Icons/Icons";

const EmployeeSalaryComponentPane = ({
  closePane,
  selectedTabIndex,
  editData,
  employee,
  month,
  year,
}) => {
  // salary component
  const { data: componentResponse } = useViewEmployeeGeneratedSalaryComponents(
    editData
      ? {
          employee_id: employee?.employee_id,
          month,
          year,
          component_id: editData?.component_id,
        }
      : {},
    { enabled: !!employee && !!editData }
  );

  const componentData = Array.isArray(componentResponse?.data)
    ? componentResponse.data[0]
    : null;

    const [isCurrentMonth, setIsCurrentMonth] = useState(true);


  const {
    register,
    handleSubmit,
    control,
    watch,
    formState: { errors },
    reset,
  } = useForm({
    defaultValues: {
      componentName: "",
      to_be_calculated_value: "",
      calculationType: null,
      calculationFrom: null,
    },
  });

  useEffect(() => {
    if (componentData) {
      reset({
        componentName: componentData.component_name || "",
        to_be_calculated_value: componentData.to_be_calculated_value || "",
        calculationType:
          calculationTypeOptions.find(
            (opt) =>
              Number(opt.value) === Number(componentData.calculation_type)
          ) || null,
        calculationFrom:
          calculationFromOptions.find(
            (opt) =>
              Number(opt.value) === Number(componentData.calculated_from_value)
          ) || null,
      });
    }
  }, [componentData, reset]);

  const [selectedStatutoryComponentIds, setSelectedStatutoryComponentIds] =
    useState(
      Array.isArray(componentData?.statutory_component_ids)
        ? componentData.statutory_component_ids
        : []
    );
  const [isActive, setIsActive] = useState(
    componentData?.is_active === true || componentData?.is_active === "true"
  );
  const [isHideInPayslip, setIsHideInPayslip] = useState(
    componentData?.hide_in_payslip === true
  );

  const [componentType, setComponentType] = useState(null);

  useEffect(() => {
    if (componentData) {
      setComponentType(
        componentTypeOptions.find(
          (opt) => Number(opt.value) === Number(componentData.component_type)
        )
      );
    } else {
      setComponentType(
        componentTypeOptions.find(
          (opt) =>
            opt.value ===
            (selectedTabIndex === 0
              ? SALARY_COMPONENT_TYPES.EARNING
              : SALARY_COMPONENT_TYPES.DEDUCTION)
        )
      );
    }
  }, [componentData, selectedTabIndex]);

  const createOrUpdateEmployeeSalaryComponent =
    useCreateOrUpdateEmployeeSalaryComponents();
  const selectedCalculationType = watch("calculationType");

  const getValueLabel = () =>
    Number(selectedCalculationType?.value) ===
    Number(SALARY_COMPONENT_CALCULATION.PERCENTAGE)
      ? "Percentage (%)"
      : "Fixed Amount";

  const onSubmit = (formData) => {
    const payload = {
      employee_id: employee?.employee_id,
      component_name: formData.componentName,
      to_be_calculated_value: parseFloat(formData.to_be_calculated_value),
      calculation_type: parseInt(formData.calculationType.value),
      component_type: componentType?.value,
      hide_in_payslip: isHideInPayslip.toString(),
      is_active: isActive.toString(),
      component_id: editData ? editData.component_id : null,

      year: year,
      statutory_component_ids: selectedStatutoryComponentIds.filter(
        (id) => id != null
      ),
    };
     if (isCurrentMonth) {
    payload.month = month;
  }
    if (formData.calculationFrom) {
      payload.calculated_from = formData.calculationFrom.label;
      payload.calculated_from_value = parseInt(formData.calculationFrom.value);
    }

    if (componentResponse?.salary_component_id) {
      payload.salary_component_id = componentResponse.salary_component_id;
    }

    createOrUpdateEmployeeSalaryComponent.mutate(payload, {
      onSuccess: (response) => {
        if (response.status) {
          toast.success(
            response.message ||
              (componentResponse
                ? "Salary component updated successfully"
                : "Salary component created successfully")
          );
          closePane();
        } else {
          toast.error(response.message || "Failed to save salary component");
        }
      },
      onError: (error) => {
        toast.error(
          error?.response?.data?.message ||
            "Error while saving salary component"
        );
      },
    });
  };

  return (
    <div className="p-4">
      <form onSubmit={handleSubmit(onSubmit)}>
        {/* Component Type */}
        <div className="mb-4">
          <div className="flex items-center gap-1">
            <label className="block text-sm font-medium">Component Type</label>
            <Tooltip title="Select whether this is an earning or deduction component.">
              <span className="cursor-pointer">{infoIcon}</span>
            </Tooltip>
          </div>
          <Select
            options={componentTypeOptions}
            value={componentType}
            onChange={(selected) => setComponentType(selected)}
            placeholder="Select Component Type"
          />
        </div>

        {/* Component Name */}
        <div className="mb-4">
          <div className="flex items-center gap-1">
            <label className="block text-sm font-medium">Component Name</label>
            <Tooltip title="Enter the name of the salary component (e.g., HRA, Bonus, PF).">
              <span className="cursor-pointer">{infoIcon}</span>
            </Tooltip>
          </div>
       <input
  type="text"
  className={`border p-2 w-full ${editData ? "bg-gray-100 cursor-not-allowed" : ""}`}
  disabled={!!editData} 
  {...register("componentName", {
    required: "This field is required",
  })}
/>
          {errors.componentName && (
            <p className="text-red-500 text-xs">
              {errors.componentName.message}
            </p>
          )}
        </div>

        {/* Calculation Type */}
        <div className="mb-4">
          <div className="flex items-center gap-1">
            <label className="block text-sm font-medium">
              Calculation Type
            </label>
            <Tooltip title="Choose how this component's amount should be calculated (e.g., fixed, percentage, or auto).">
              <span className="cursor-pointer">{infoIcon}</span>
            </Tooltip>
          </div>

          <Controller
            name="calculationType"
            control={control}
            render={({ field }) => (
              <Select
                {...field}
                options={calculationTypeOptions}
                placeholder="Select Calculation Type"
              />
            )}
          />

          {errors.calculationType && (
            <p className="text-red-500 text-xs">
              {errors.calculationType.message}
            </p>
          )}
        </div>

        {/* Calculation Value */}
        {selectedCalculationType &&
          (Number(selectedCalculationType?.value) ===
          Number(SALARY_COMPONENT_CALCULATION.AUTO_CALCULATED) ? (
            <div className="bg-yellow-100 border-l-4 border-yellow-500 text-yellow-700 p-3 mb-4 text-sm rounded">
              <p>
                <strong>Note:</strong> This component is calculated
                automatically.
              </p>
            </div>
          ) : (
            <div className="mb-4">
              <label className="block text-sm font-medium">
                Calculation Based on ({getValueLabel()})
              </label>
              <input
                type="number"
                step="0.01"
                className="border p-2 w-full"
                {...register("to_be_calculated_value", {
                  required: "This field is required",
                  valueAsNumber: true,
                  validate: (value) => {
                    if (
                      Number(selectedCalculationType?.value) ===
                      SALARY_COMPONENT_CALCULATION.PERCENTAGE
                    ) {
                      return (
                        (value >= 0 && value <= 100) ||
                        "Percentage must be between 0 and 100"
                      );
                    }
                    return true;
                  },
                })}
              />
              {errors.to_be_calculated_value && (
                <p className="text-red-500 text-xs">
                  {errors.to_be_calculated_value.message}
                </p>
              )}
            </div>
          ))}

        {/* Calculation From */}
        {selectedCalculationType &&
          Number(selectedCalculationType?.value) !==
            Number(SALARY_COMPONENT_CALCULATION.AUTO_CALCULATED) && (
            <div className="mb-4">
              <div className="flex items-center gap-1">
                <label className="block text-sm font-medium">
                  Calculation From
                </label>
                <Tooltip title="Select the base component or value from which this component will be calculated.">
                  <span className="cursor-pointer">{infoIcon}</span>
                </Tooltip>
              </div>

              <Controller
                name="calculationFrom"
                control={control}
                render={({ field }) => (
                  <Select
                    {...field}
                    options={calculationFromOptions}
                    placeholder="Select Calculation From"
                  />
                )}
              />

              {errors.calculationFrom && (
                <p className="text-red-500 text-xs">
                  {errors.calculationFrom.message}
                </p>
              )}
            </div>
          )}

        {/* <div className="mb-4">
            <div className="flex items-center gap-1">
              <label className="block text-sm font-medium">
                Applicable Statutory Components
              </label>
              <Tooltip title="Choose the applicable statutory components that this salary component should be considered for.">
                <span className="cursor-pointer">{infoIcon}</span>
              </Tooltip>
            </div>
            {isStatutoryLoading ? (
              <p>Loading statutory components...</p>
            ) : (
              <Select
                isMulti
                options={mandatoryStatutoryComponents.map((component) => ({
                  value: component.component_id,
                  label: component.component_name,
                }))}
                value={mandatoryStatutoryComponents
                  .filter((comp) =>
                    selectedStatutoryComponentIds.includes(comp.component_id)
                  )
                  .map((comp) => ({
                    value: comp.component_id,
                    label: comp.component_name,
                  }))}
                onChange={(selectedOptions) => {
                  setSelectedStatutoryComponentIds(
                    selectedOptions.map((option) => option.value)
                  );
                }}
                placeholder="Select statutory components"
              />
            )}
          </div> */}

        <div className="mb-4 flex items-center justify-between">
          <label className="block text-sm font-medium">Is Active</label>
          <ToggleSwitch checked={isActive} getToggleState={setIsActive} />
        </div>
        <div className="mb-4 flex items-center justify-between">
  <label className="block text-sm font-medium">For Current Month</label>
  <ToggleSwitch checked={isCurrentMonth} getToggleState={setIsCurrentMonth} />
</div>


        <div className="mb-4 flex items-center justify-between">
          <label className="block text-sm font-medium">Hide In Payslip</label>
          <ToggleSwitch
            checked={isHideInPayslip}
            getToggleState={setIsHideInPayslip}
          />
        </div>

        {/* Buttons */}
        <div className="flex gap-4">
          <button
            type="button"
            className="bg-gray-500 text-white px-4 py-2 rounded"
            onClick={closePane}
          >
            Cancel
          </button>
          <button
            type="submit"
            className="bg-blue-500 text-white px-4 py-2 rounded"
            disabled={createOrUpdateEmployeeSalaryComponent.isLoading}
          >
            {createOrUpdateEmployeeSalaryComponent.isLoading
              ? componentResponse
                ? "Updating..."
                : "Adding..."
              : componentResponse
              ? "Update"
              : "Add"}{" "}
            Salary Component
          </button>
        </div>
      </form>
    </div>
  );
};

export default EmployeeSalaryComponentPane;
